import React, { useEffect, useMemo, useState } from "react";
import { InputText } from "primereact/inputtext";
import { Dropdown, DropdownChangeEvent } from "primereact/dropdown";
import Message from "shared/Message";
import ButtonRounded from "shared/Button";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAuth } from "contexts/auth";
import { environments } from "constants/environments";
import { IApiMetaData } from "interfaces/apiMetaData";
import { callApi } from "apiServices/callApi";
import {
  createSubCategorySchema,
  ICreateSubCategoryInputs,
} from "schema/subCategory";

interface DropdownItem {
  name: string;
  value: boolean | string;
}

interface IProps {
  onSave: () => void;
}

const AddCategory = ({ onSave }: IProps) => {
  const [, setDropdownCategoryStatus] = useState<DropdownItem | null>(null);
  const [, setCategorySelected] = useState<string | null>(null);
  const [categories, setCategories] = useState<DropdownItem[]>([]);
  const [isResponse, setIsResponse] = useState(false);
  const [result, setResult] = useState<IApiMetaData | any>(null); // Proper type for result
  const { sharedData } = useAuth();

  const categoryAPI =
    environments.domain + sharedData?.apisList?.WebSite?.Category?.General;
  const subCategoryAPI =
    environments.domain + sharedData?.apisList?.WebSite?.SubCategory?.General;

  useEffect(() => {
    const getCategories = async () => {
      try {
        const response: IApiMetaData = await callApi(
          "GET",
          categoryAPI,
          {},
          {},
          { select: "name" }
        );
        setCategories(
          response?.Data?.Content?.map((category: any) => ({
            name: category.name,
            value: category._id,
          }))
        );
      } catch (error) {
        // Handle error
      }
    };
    getCategories();
  }, [categoryAPI]);

  const {
    handleSubmit,
    register,
    control,
    formState: { errors, isSubmitting },
  } = useForm<ICreateSubCategoryInputs>({
    resolver: zodResolver(createSubCategorySchema),
    mode: "onChange",
  });

  const onSubmit: SubmitHandler<ICreateSubCategoryInputs> = async (data) => {
    setIsResponse(false);
    try {
      const response: IApiMetaData = await callApi(
        "POST",
        subCategoryAPI,
        data,
        {}
      );
      setResult(response);
      if (response.Status === 201) {
        onSave();
      }
    } catch (error: any) {
      setResult({
        Status: error.response?.status || 500,
        Message: error.response?.data?.Error || "An error occurred",
        Severity: "error",
      });
    }
    setIsResponse(true);
  };

  const status: DropdownItem[] = useMemo(
    () => [
      { name: "Active", value: true },
      { name: "Not Active", value: false },
    ],
    []
  );

  const handleStatusChange = (
    e: DropdownChangeEvent,
    onChange: (e: any) => void
  ) => {
    onChange(e);
    setDropdownCategoryStatus(e.value);
  };

  const handleSelectProgram = (
    e: DropdownChangeEvent,
    onChange: (e: any) => void
  ) => {
    onChange(e);
    setCategorySelected(e.value);
  };

  return (
    <div className="grid justify-content-center">
      <div className="col-12">
        <div className="px-3">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="p-fluid formgrid p-4 grid border-round-md">
              <div className="field col-12 md:col-12 lg:col-6 xl:col-6">
                <label htmlFor="name">Name</label>
                <InputText
                  id="name"
                  type="text"
                  {...register("name", {
                    setValueAs: (v) => v?.trim(),
                  })}
                />
                {errors?.name && (
                  <small className="text-error text-red-600">
                    {errors?.name?.message}
                  </small>
                )}
              </div>

              <div className="field col-12 md:col-6">
                <label htmlFor="Module">Module</label>
                <Controller
                  name="category"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <Dropdown
                        id="category"
                        value={value}
                        options={categories}
                        onChange={(e) => handleSelectProgram(e, onChange)}
                        optionLabel="name"
                        optionValue="value"
                        placeholder="Select Module Name"
                      />
                      {errors?.category && (
                        <small className="text-error text-red-600">
                          {errors?.category?.message}
                        </small>
                      )}
                    </>
                  )}
                />
              </div>

              <div className="field col-12 md:col-12 lg:col-6 xl:col-6">
                <label htmlFor="status">Status</label>
                <Controller
                  name="isActive"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <>
                      <Dropdown
                        id="status"
                        value={value}
                        options={status}
                        onChange={(e) => handleStatusChange(e, onChange)}
                        optionLabel="name"
                        optionValue="value"
                        placeholder="Select Status"
                      />
                      {errors?.isActive && (
                        <small className="text-error text-red-600">
                          {errors?.isActive?.message}
                        </small>
                      )}
                    </>
                  )}
                />
              </div>

              <div className="text-right mt-4 ml-auto">
                <ButtonRounded
                  disabled={isSubmitting}
                  label="Save"
                  raised
                  severity="success"
                />
              </div>
            </div>
          </form>
        </div>
      </div>

      {isResponse && (
        <div className="col-8">
          <Message
            message={result?.Message}
            severity={result?.Status === 201 ? "success" : "error"}
          />
        </div>
      )}
    </div>
  );
};

export default AddCategory;
